<template>
  <v-dialog :model-value="modelValue" max-width="460" @update:model-value="$emit('update:modelValue', $event)">
    <v-card>
      <!-- Cancel is the title bar's close button, not a third action: three labelled buttons
           do not fit side by side on a phone. -->
      <v-card-title class="font-display d-flex align-center">
        Benachrichtigungen
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          size="small"
          aria-label="Schließen"
          title="Schließen"
          @click="$emit('update:modelValue', false)"
        />
      </v-card-title>
      <v-card-subtitle>{{ trainLabel }}</v-card-subtitle>

      <v-card-text>
        <v-alert v-if="!pushSupported" type="info" variant="tonal" density="compact" class="mb-4">
          <template v-if="pushNeedsHomeScreen">
            Auf dem iPhone gibt es Benachrichtigungen nur für Web-Apps auf dem Home-Bildschirm:
            Bahnomat über „Teilen → Zum Home-Bildschirm" hinzufügen und von dort öffnen.
          </template>
          <template v-else>Dieser Browser unterstützt keine Push-Benachrichtigungen.</template>
        </v-alert>
        <v-alert v-else-if="notificationPermission === 'denied'" type="warning" variant="tonal" density="compact" class="mb-4">
          Benachrichtigungen sind für Bahnomat blockiert. Sie lassen sich in den Website-Einstellungen des Browsers erlauben.
        </v-alert>

        <v-select v-model="fromEva" :items="fromItems" label="Einstieg" class="mb-2" />
        <v-select v-model="toEva" :items="toItems" label="Ausstieg" class="mb-2" />

        <div class="text-subtitle-2 mb-1">Melden bei</div>
        <v-checkbox
          v-for="event in eventOptions"
          :key="event.value"
          v-model="events"
          :value="event.value"
          :label="event.title"
          density="compact"
          hide-details
        />

        <v-select
          v-if="events.includes('DELAY')"
          v-model="threshold"
          :items="thresholdItems"
          label="Verspätung melden ab einer Änderung von"
          class="mt-4"
        />

        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mt-2">{{ error }}</v-alert>
      </v-card-text>

      <!-- Icon-only on a phone, where the labelled buttons do not fit; labelled (with the same
           icon) wherever there is room, since a label is clearer than an icon alone. -->
      <v-card-actions>
        <template v-if="existing">
          <v-btn
            v-if="compact"
            icon="mdi-bell-off-outline"
            color="error"
            variant="text"
            :loading="removing"
            aria-label="Benachrichtigung deaktivieren"
            title="Benachrichtigung deaktivieren"
            @click="remove"
          />
          <v-btn
            v-else
            prepend-icon="mdi-bell-off-outline"
            color="error"
            variant="text"
            :loading="removing"
            @click="remove"
          >Deaktivieren</v-btn>
        </template>
        <v-spacer />
        <v-btn
          v-if="compact"
          :icon="saveAction.icon"
          color="primary"
          variant="flat"
          :disabled="!canSave"
          :loading="saving"
          :aria-label="saveAction.description"
          :title="saveAction.description"
          @click="save"
        />
        <v-btn
          v-else
          :prepend-icon="saveAction.icon"
          color="primary"
          variant="flat"
          :disabled="!canSave"
          :loading="saving"
          @click="save"
        >{{ saveAction.label }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import DateTimeMixin from '@/mixins/DateTimeMixin'
import NotificationMixin, { NOTIFICATION_EVENTS } from '@/mixins/NotificationMixin'

const THRESHOLDS = [3, 5, 10, 15, 20, 30]

export default {
  name: 'NotificationDialog',
  mixins: [DateTimeMixin, NotificationMixin],
  props: {
    modelValue: Boolean,
    tripId: String,
    trainLabel: String,
    stops: Array,
  },
  emits: ['update:modelValue'],

  data: () => ({
    fromEva: null,
    toEva: null,
    events: NOTIFICATION_EVENTS.map(event => event.value),
    threshold: 5,
    saving: false,
    removing: false,
    error: null,
  }),

  computed: {
    eventOptions: function() {
      return NOTIFICATION_EVENTS
    },

    thresholdItems: function() {
      return THRESHOLDS.map(minutes => ({ value: minutes, title: `${minutes} min` }))
    },

    // Stops a traveller can board or leave at: known station, not cancelled —
    // except the ends of an existing subscription, which stay selectable even
    // after IRIS cancelled them, so the dialog can still show what was chosen.
    selectableStops: function() {
      var existing = this.existing
      var kept = existing ? [existing.fromEva, existing.toEva] : []
      return (this.stops || []).filter(stop => stop.evaStationId
        && (!stop.cancelled || kept.includes(stop.evaStationId)))
    },

    fromItems: function() {
      return this.selectableStops.slice(0, -1).map(stop => ({
        value: stop.evaStationId,
        title: `${stop.station} · ${this.toTime(this.plannedTime(stop.departure || stop.arrival))}`,
      }))
    },

    toItems: function() {
      var start = this.selectableStops.findIndex(stop => stop.evaStationId === this.fromEva)
      return this.selectableStops.slice(start + 1).map(stop => ({
        value: stop.evaStationId,
        title: `${stop.station} · ${this.toTime(this.plannedTime(stop.arrival || stop.departure))}`,
      }))
    },

    existing: function() {
      return this.notificationFor(this.tripId)
    },

    compact: function() {
      return this.$vuetify.display.xs
    },

    // A disk only says "save"; a new subscription is switched on, hence the bell.
    saveAction: function() {
      return this.existing
        ? { icon: 'mdi-content-save-outline', label: 'Speichern', description: 'Speichern' }
        : { icon: 'mdi-bell-plus-outline', label: 'Aktivieren', description: 'Benachrichtigung aktivieren' }
    },

    canSave: function() {
      return this.pushSupported && this.notificationPermission !== 'denied'
        && this.fromEva && this.toEva && this.events.length > 0
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler: function(open) {
        if (open) this.reset()
      },
    },

    // A boarding stop after the chosen alighting stop would leave no segment.
    fromEva: function() {
      if (!this.toItems.some(item => item.value === this.toEva)) {
        var last = this.toItems[this.toItems.length - 1]
        this.toEva = last ? last.value : null
      }
    },
  },

  methods: {
    plannedTime: function(arrivalOrDeparture) {
      return arrivalOrDeparture ? arrivalOrDeparture.plannedTime : null
    },

    reset: function() {
      this.error = null
      var existing = this.existing
      var stops = this.selectableStops
      if (existing) {
        // toEva first: the fromEva watcher re-clamps toEva against the new
        // boarding stop and must see the stored alighting stop, not the old one.
        this.toEva = existing.toEva
        this.fromEva = existing.fromEva
        this.events = [...existing.events]
        this.threshold = existing.delayThresholdMinutes
      } else {
        this.fromEva = stops.length ? stops[0].evaStationId : null
        this.toEva = stops.length > 1 ? stops[stops.length - 1].evaStationId : null
        this.events = NOTIFICATION_EVENTS.map(event => event.value)
        this.threshold = 5
      }
    },

    save: async function() {
      this.saving = true
      this.error = null
      try {
        await this.subscribeNotifications({
          tripId: this.tripId,
          fromEva: this.fromEva,
          toEva: this.toEva,
          events: this.events,
          delayThresholdMinutes: this.threshold,
        })
        this.$emit('update:modelValue', false)
      } catch (err) {
        this.error = err.message
      } finally {
        this.saving = false
      }
    },

    remove: async function() {
      this.removing = true
      this.error = null
      try {
        await this.unsubscribeNotifications(this.tripId)
        this.$emit('update:modelValue', false)
      } catch (err) {
        this.error = err.message
      } finally {
        this.removing = false
      }
    },
  },
}
</script>
